/**
 * AudioReactor — the `?` key's music-reactivity mode (mic → beat clock).
 *
 * Like TapTempo, it's a pure paramBus *producer*: it writes only `tempo.anchorMs`
 * and `tempo.bpm` and never touches the renderer. When toggled on it opens the
 * microphone, analyses the low-frequency energy each frame with a BeatDetector, and
 * on every detected onset re-anchors the beat clock — the exact same params Space
 * taps write. Because the scene already derives its per-frame beat envelope from
 * bpm + anchor (see Visualizer.beatEnvelope), every beat-locked effect (strobe,
 * ripple, explode, bloom, zoom, the live pulse) reacts to the music for free, and
 * the audio simply **overrides** whatever tap tempo had set.
 *
 * The `+`/`−` keys scrub detection **sensitivity** (0..1) while music is active,
 * modelled exactly like ModifierController's per-effect knob: a continuous per-frame
 * ramp gated by `suppressed` so a held palette/visual/effect key wins the keys. The
 * browser bits (getUserMedia / AudioContext / rAF) are isolated here and only run
 * in `start()`; the beat logic lives in the unit-tested BeatDetector.
 */

import { ParamBus } from '../state/paramBus';
import { BeatDetector } from './beatDetector';
import { PLUS_KEYS, MINUS_KEYS, MODIFIER_RATE } from '../input/modifierController';

/** FFT size for the analyser — 1024 bins is plenty for bass onset detection. */
const FFT_SIZE = 1024;
/** Fraction of the low spectrum treated as "bass" (where kick/beat energy lives). */
const BASS_FRACTION = 1 / 8;
/** Starting sensitivity (0..1) before any `+`/`−` scrub. */
const DEFAULT_SENSITIVITY = 0.5;

type AudioContextCtor = typeof AudioContext;

export class AudioReactor {
  private activeFlag = false;
  private sensitivityValue = DEFAULT_SENSITIVITY;
  private readonly detector: BeatDetector;

  // Browser audio graph — all undefined until start() succeeds.
  private stream?: MediaStream;
  private ctx?: AudioContext;
  private analyser?: AnalyserNode;
  private freq?: Uint8Array;
  private raf = 0;

  // `+`/`−` scrub state (mirrors ModifierController).
  private lastMs = -1;
  private readonly modKeys = new Set<string>();

  constructor(
    private readonly bus: ParamBus,
    private readonly now: () => number = defaultNow,
    detector: BeatDetector = new BeatDetector(DEFAULT_SENSITIVITY),
  ) {
    this.detector = detector;
    this.detector.setSensitivity(this.sensitivityValue);
  }

  get active(): boolean {
    return this.activeFlag;
  }

  /** Current detection sensitivity, 0..1 (shown by the modify label). */
  get sensitivity(): number {
    return this.sensitivityValue;
  }

  /** Toggle music reactivity. Resolves to the resulting `active` state. */
  toggle(): Promise<boolean> {
    if (this.activeFlag) {
      this.stop();
      return Promise.resolve(false);
    }
    return this.start();
  }

  /** Open the mic and start the analysis loop. Resolves false on denial/failure. */
  private async start(): Promise<boolean> {
    try {
      const AC: AudioContextCtor | undefined =
        (globalThis as { AudioContext?: AudioContextCtor; webkitAudioContext?: AudioContextCtor })
          .AudioContext ??
        (globalThis as { webkitAudioContext?: AudioContextCtor }).webkitAudioContext;
      if (!AC || !navigator.mediaDevices?.getUserMedia) return false;

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
      });
      const ctx = new AC();
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = FFT_SIZE;
      analyser.smoothingTimeConstant = 0.6;
      source.connect(analyser);

      this.stream = stream;
      this.ctx = ctx;
      this.analyser = analyser;
      this.freq = new Uint8Array(analyser.frequencyBinCount);
      this.activeFlag = true;
      this.loop();
      return true;
    } catch {
      // Permission denied or no device — stay off, cleaning up any partial graph.
      this.stop();
      return false;
    }
  }

  /** Stop the loop, release the mic, and close the context. Safe when inactive. */
  stop(): void {
    this.activeFlag = false;
    if (this.raf) {
      cancelAnimationFrame(this.raf);
      this.raf = 0;
    }
    this.stream?.getTracks().forEach((t) => t.stop());
    this.ctx?.close().catch(() => {});
    this.stream = undefined;
    this.ctx = undefined;
    this.analyser = undefined;
    this.freq = undefined;
  }

  private loop = (): void => {
    if (!this.activeFlag || !this.analyser || !this.freq) return;
    this.analyser.getByteFrequencyData(this.freq as Uint8Array<ArrayBuffer>);
    const energy = bassEnergy(this.freq);
    const now = this.now();
    const { beat, bpm } = this.detector.push(energy, now);
    if (beat) {
      this.bus.set('tempo.anchorMs', now);
      if (bpm > 0) this.bus.set('tempo.bpm', bpm);
    }
    this.raf = requestAnimationFrame(this.loop);
  };

  // ── `+`/`−` sensitivity scrub (fed the same key stream as everything else) ──
  keyDown(key: string): void {
    if (PLUS_KEYS.includes(key) || MINUS_KEYS.includes(key)) this.modKeys.add(key);
  }

  keyUp(key: string): void {
    this.modKeys.delete(key);
  }

  /** Advance the sensitivity ramp while `+`/`−` is held and music is active. */
  tick(nowMs: number, suppressed = false): void {
    const dt = this.lastMs < 0 ? 0 : (nowMs - this.lastMs) / 1000;
    this.lastMs = nowMs;
    if (!this.activeFlag || suppressed || dt <= 0) return;
    const dir = this.direction();
    if (dir === 0) return;
    this.setSensitivity(this.sensitivityValue + dir * MODIFIER_RATE * dt);
  }

  private setSensitivity(v: number): void {
    this.sensitivityValue = round2(clamp01(v));
    this.detector.setSensitivity(this.sensitivityValue);
  }

  /** +1 while a plus key is held, -1 for minus, 0 for neither/both. */
  private direction(): number {
    const up = PLUS_KEYS.some((k) => this.modKeys.has(k));
    const down = MINUS_KEYS.some((k) => this.modKeys.has(k));
    return (up ? 1 : 0) - (down ? 1 : 0);
  }
}

/** Mean of the lowest `BASS_FRACTION` of the spectrum, normalized to ~0..1. */
function bassEnergy(freq: Uint8Array): number {
  const bins = Math.max(1, Math.floor(freq.length * BASS_FRACTION));
  let sum = 0;
  for (let i = 0; i < bins; i++) sum += freq[i];
  return sum / bins / 255;
}

function defaultNow(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

function round2(v: number): number {
  return Math.round(v * 100) / 100;
}
